import { OrganizationLevelType } from '@/api/organizations';
import { fetchReportPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import { availableReportsQueryOptions } from '@/api/reportQueryOptions';
import { fetchDatenblaetter, fetchLebenslaeufe, fetchReport, ReportDefinition } from '@/api/reports';
import { sectionsUsedQueryOptions } from '@/api/sectionsQueryOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import { exportBlob } from '@/lib/exportUtils';
import { getOrganization } from '@/store/auth/authSlice';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import { Avatar, Button, List, ListItemAvatar, ListItemButton, ListItemText, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Buffer } from 'buffer';
import { enqueueSnackbar } from 'notistack';
import { FaRegFileExcel } from "react-icons/fa";
import { useSelector } from 'react-redux';

export const Route = createFileRoute('/_auth/reports')({
    component: ReportsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

const reportDatenblaetter = (sectionUuid: string) => {
    fetchDatenblaetter(sectionUuid).then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw], {
                type: 'application/zip',
            });
            exportBlob(blob, response.data.filename || 'Datenblaetter.zip');
        }
    })
}

const reportLebenslaeufe = (sectionUuid: string) => {
    fetchLebenslaeufe(sectionUuid).then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw], {
                type: 'application/zip',
            });
            exportBlob(blob, response.data.filename || 'Lebensläufe.zip');
        }
    })
}

const report = (report: ReportDefinition) => {
    enqueueSnackbar(`Der Bericht "${report.name}" wird erstellt. Dies kann je nach Größe des Berichts einige Zeit dauern.`, { variant: 'info' });
    fetchReport(report.url).then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            const raw = Buffer.from(base64, 'base64')
            const blob = new Blob([raw], {
                type: report.mimetype,
            });
            enqueueSnackbar(`Der Bericht "${report.name}" wurde erstellt und wird heruntergeladen.`, { variant: 'success' });
            exportBlob(blob, response.data.filename || (report.filename));
        }
    }).catch(() => {
        enqueueSnackbar(`Fehler beim Erstellen des Berichts "${report.name}"`, { variant: 'error' });
    })
}

function ReportsComponent() {

    const sectionQuery = useQuery(sectionsUsedQueryOptions());
    const sections = sectionQuery.data;

    const sessionOrganization = useSelector(getOrganization);

    const reportPermissionQuery = useSuspenseQuery(fetchReportPermissionsQueryOptions(['Pfadfinderlebenslauf_section', 'Datenblatt_section']))
    const reportPermissions = reportPermissionQuery.data

    const availableReportsQuery = useSuspenseQuery(availableReportsQueryOptions())
    const availableReports = availableReportsQuery.data

    const pfadfinderlebenslaufVisible = reportPermissions && reportPermissions.filter(p => p.detail === 'Pfadfinderlebenslauf_section' && p.permissions.some(a => a === 'read')).length > 0
    const datenblattVisible = reportPermissions && reportPermissions.filter(p => p.detail === 'Datenblatt_section' && p.permissions.some(a => a === 'read')).length > 0

    const sectionsvisible = sessionOrganization?.organizationLevel === OrganizationLevelType.TROOP && (pfadfinderlebenslaufVisible || datenblattVisible)

    if (availableReportsQuery.isLoading || reportPermissionQuery.isFetching) {
        return <div>Loading...</div>
    }

    return <>
        <Typography variant='h6' gutterBottom>Berichte</Typography>
        <List>
            {availableReports.map((reportDef) => (
                <ListItemButton key={reportDef.url} onClick={() => report(reportDef)}>
                    <ListItemAvatar>
                        <Avatar>
                            {'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' === reportDef.mimetype && <FaRegFileExcel />}
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={reportDef.name} secondary={reportDef.description} />
                </ListItemButton>
            ))}
        </List>
        {sections && sectionsvisible && (<>
            <Typography variant='h6' gutterBottom>Stufen</Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Stufe</TableCell>
                            {datenblattVisible &&
                                <TableCell>Datenblätter</TableCell>
                            }
                            {pfadfinderlebenslaufVisible &&
                                <TableCell>Lebensläufe</TableCell>
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            sections.map(section =>
                                <TableRow key={section.uuid}>
                                    <TableCell>
                                        {section.name}
                                    </TableCell>
                                    {datenblattVisible &&
                                        <TableCell>
                                            <Button
                                                startIcon={<FolderZipIcon />}
                                                fullWidth={true}
                                                style={{ justifyContent: "flex-start", textTransform: "none" }}
                                                size="medium"
                                                onClick={() => reportDatenblaetter(section.uuid || '')}>
                                            </Button>
                                        </TableCell>
                                    }
                                    {pfadfinderlebenslaufVisible &&
                                        <TableCell>
                                            <Button
                                                startIcon={<FolderZipIcon />}
                                                fullWidth={true}
                                                style={{ justifyContent: "flex-start", textTransform: "none" }}
                                                size="medium"
                                                onClick={() => reportLebenslaeufe(section.uuid || '')}>
                                            </Button>
                                        </TableCell>
                                    }
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </>)}
    </>
}
